/**
 * The stock reports.
 *
 *   Stock on hand   what is where, and -- for whoever may see costs --
 *                   what it is worth, set against Stock in Hand in the
 *                   books. A difference is said out loud, not hidden.
 *   Stock card      one item's every movement, opening to closing, each
 *                   row opening the document behind it.
 *   Van stock       what each van holds now: the morning load, the
 *                   evening check.
 *   Sales by item   what sold, and what it made.
 *   Reorder         items below their reorder level.
 *
 * Every figure comes from the server's report, which reads the same stock
 * moves the books were posted from. Quantities are shown the way the shelf
 * is counted -- 10 CTN + 4 PCS.
 */
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Paper, Skeleton, Stack, TextField, MenuItem, Typography, useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import {
  fetchInvGroups, fetchInvItems, fetchLowStock, fetchSalesByItem, fetchStockCard,
  fetchStockReport, fetchStores, fetchVanStock,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { InvItemRef, StockCardRow } from '../api/types'
import ReportShell, { rangeLabel } from '../reports/ReportShell'
import ReportToolbar from '../reports/ReportToolbar'
import ReportFooter from '../reports/ReportFooter'
import { useDateRange } from '../reports/useDateRange'
import { Figure, FigureGrid } from '../reports/SummaryFigures'
import { useBi } from '../components/Bi'
import { downloadCsv } from '../reports/csv'
import { gregorian } from '../reports/hijri'
import { c, fmtMoney } from '../theme'
import { fmtQty } from './qty'
import { ItemPicker, StoreSelect, packsOf } from './bits'

const nameOf = (r: InvItemRef, ar: boolean) => (ar && r.item_name_ar) || r.item_name

function Loading() { return <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box> }
function Empty({ text }: { text: string }) {
  return <Typography sx={{ p: 3, color: c.inkMuted }}>{text}</Typography>
}

function GroupSelect({ value, onChange }: { value: number | ''; onChange: (v: number | '') => void }) {
  const { t } = useTranslation()
  const groups = useQuery({ queryKey: ['inv-groups'], queryFn: fetchInvGroups })
  return (
    <TextField select size="small" label={t('inv.group')} value={value} sx={{ minWidth: 170 }}
      SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}>
      <MenuItem value="">{t('inv.allGroups')}</MenuItem>
      {(groups.data ?? []).map((g) => <MenuItem key={g.id} value={g.id}>{g.name_en}</MenuItem>)}
    </TextField>
  )
}

// --- stock on hand -----------------------------------------------------------

export function StockOnHandPage() {
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const nav = useNavigate()
  const ar = i18n.language === 'ar'
  const narrow = useMediaQuery('(max-width:700px)')
  const { range, setRange, presets } = useDateRange()
  const [store, setStore] = useState<number | ''>('')
  const [group, setGroup] = useState<number | ''>('')
  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const params = { as_of: range.to, store_id: store || undefined, group_id: group || undefined }
  const q = useQuery({ queryKey: ['inv-stock-report', params], queryFn: () => fetchStockReport(params) })
  const rows = q.data?.rows ?? []
  const seesValue = q.data?.total_value !== undefined
  const card = (id: number) => nav(`/inventory/stock-card?item=${id}${store ? `&store=${store}` : ''}`)

  return (
    <ReportShell titleKey="nav.stockOnHand" period={`${t('report.asOf')} ${gregorian(range.to, i18n.language)}`}
      toolbar={
        <ReportToolbar singleDate range={range} setRange={setRange} presets={presets}
          pdf={{ path: '/inventory/reports/stock.pdf',
                 params: { as_of: range.to, store_id: store ? String(store) : undefined,
                           group_id: group ? String(group) : undefined },
                 filename: `stock-on-hand-${range.to}.pdf` }}
          onExport={() => downloadCsv(`stock-on-hand-${range.to}`, [
            ['Code', 'Item', 'Group', 'Base unit', 'Quantity', ...(seesValue ? ['Avg cost', 'Value'] : [])],
            ...rows.map((r) => [r.item_code, r.item_name, r.group_name ?? '', r.base_unit, r.qty,
              ...(seesValue ? [r.avg_cost ?? '', r.value ?? ''] : [])]),
          ])}>
          <StoreSelect label={t('inv.store')} value={store} stores={stores.data ?? []}
            onChange={setStore} allowAll sx={{ minWidth: 200 }} />
          <GroupSelect value={group} onChange={setGroup} />
        </ReportToolbar>
      }>
      {q.isError ? <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>
        : q.isLoading ? <Loading /> : (
          <>
            {seesValue && (
              <FigureGrid>
                <Figure label={bi('stock.items')} value={String(q.data!.count)} />
                <Figure label={bi('stock.stockValue')} value={fmtMoney(q.data!.total_value!)} strong />
                {q.data!.books_value != null && (
                  <Figure label={bi('stock.booksValue')} value={fmtMoney(q.data!.books_value)} />
                )}
                {q.data!.difference != null && (
                  <Figure label={bi('inv.difference')} value={fmtMoney(q.data!.difference)}
                    tone={q.data!.agrees ? undefined : 'danger'} />
                )}
              </FigureGrid>
            )}
            {seesValue && q.data!.difference != null && (
              <Alert severity={q.data!.agrees ? 'success' : 'warning'} sx={{ mx: 2.5, mb: 2 }}>
                {Number(q.data!.difference) === 0 ? t('stock.agrees')
                  : q.data!.agrees ? t('stock.rounding', { diff: fmtMoney(q.data!.difference) })
                    : t('stock.disagrees', { diff: fmtMoney(q.data!.difference),
                                             ledger: q.data!.stock_ledger?.name ?? '' })}
              </Alert>
            )}
            {rows.length === 0 ? <Empty text={t('stock.noStock')} /> : narrow ? (
              <Box className="ruled">
                {rows.map((r) => (
                  <Box key={r.item_id} component="button" type="button" onClick={() => card(r.item_id)}
                    sx={{ display: 'flex', width: '100%', gap: 1.5, px: 2, py: 1.25, border: 0,
                          bgcolor: 'transparent', font: 'inherit', color: 'inherit', textAlign: 'start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap dir="auto" sx={{ fontWeight: 500 }}>{nameOf(r, ar)}</Typography>
                      <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
                        <span className="doc-no">{r.item_code}</span>{r.value !== undefined && ` · ${fmtMoney(r.value)}`}</Typography>
                    </Box>
                    <Typography className="num" sx={{ fontWeight: 600 }}>{packsOf(r.qty, r)}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ px: 2, pb: 1, overflowX: 'auto' }}>
                <table className="rpt" style={{ minWidth: 760 }}>
                  <thead><tr>
                    <th style={{ width: 110 }}>{t('inv.code')}</th>
                    <th>{t('inv.item')}</th>
                    <th style={{ width: 140 }}>{t('inv.group')}</th>
                    <th className="num" style={{ width: 150 }}>{t('inv.qty')}</th>
                    <th className="num" style={{ width: 110 }}>{t('stock.inBase')}</th>
                    {seesValue && <th className="num" style={{ width: 110 }}>{t('inv.avgCost')}</th>}
                    {seesValue && <th className="num" style={{ width: 120 }}>{t('inv.value')}</th>}
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.item_id} className="clickable" onClick={() => card(r.item_id)}>
                        <td><span className="doc-no">{r.item_code}</span></td>
                        <td dir="auto">{nameOf(r, ar)}
                          {r.reorder_level != null && Number(r.qty) < Number(r.reorder_level) && (
                            <span style={{ marginInlineStart: 8, fontSize: 11.5, color: 'var(--warning-ink)' }}>
                              {t('stock.belowReorder')}</span>)}
                        </td>
                        <td style={{ color: 'var(--ink-muted)' }}>{r.group_name ?? ''}</td>
                        <td className="num" style={{ color: Number(r.qty) < 0 ? c.dangerInk : undefined }}>
                          {packsOf(r.qty, r)}</td>
                        <td className="num" style={{ color: 'var(--ink-muted)' }}>{fmtQty(r.qty)} {r.base_unit}</td>
                        {seesValue && <td className="num">{Number(r.avg_cost ?? 0).toFixed(4)}</td>}
                        {seesValue && <td className="num">{fmtMoney(r.value ?? '0')}</td>}
                      </tr>
                    ))}
                  </tbody>
                  {seesValue && (
                    <tfoot><tr>
                      <td colSpan={6}>{t('report.total')}</td>
                      <td className="num">{fmtMoney(q.data!.total_value!)}</td>
                    </tr></tfoot>
                  )}
                </table>
              </Box>
            )}
          </>
        )}
      <Typography variant="caption" sx={{ display: 'block', px: 2.5, pb: 2, color: c.inkMuted }}>
        {t('stock.sohNote')}</Typography>
      <ReportFooter />
    </ReportShell>
  )
}

// --- stock card --------------------------------------------------------------

const DOC_PATH: Record<string, string> = {
  sale: '/inventory/sales', purchase: '/inventory/purchases',
  transfer: '/inventory/transfers', adjustment: '/inventory/counts',
}

export function StockCardPage() {
  const { t, i18n } = useTranslation()
  const nav = useNavigate()
  const ar = i18n.language === 'ar'
  const [search, setSearch] = useSearchParams()
  const { range, setRange, presets } = useDateRange()
  const itemId = Number(search.get('item')) || null
  const store = Number(search.get('store')) || ''
  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const items = useQuery({ queryKey: ['inv-items', '', '', 'all'], queryFn: () => fetchInvItems() })
  const item = useMemo(() => items.data?.items.find((i) => i.id === itemId) ?? null,
                       [items.data, itemId])
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(search)
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k) }
    setSearch(next, { replace: true })
  }
  const params = { from_date: range.from, to_date: range.to, store_id: store || undefined }
  const q = useQuery({ queryKey: ['inv-stock-card', itemId, params],
                       queryFn: () => fetchStockCard(itemId!, params), enabled: !!itemId })
  const d = q.data
  const open = (r: StockCardRow) => {
    if (r.doc_type && r.doc_id) nav(`${DOC_PATH[r.doc_type]}/${r.doc_id}`)
  }

  return (
    <ReportShell titleKey="nav.stockCard"
      period={rangeLabel(range.from, range.to, i18n.language, t)}
      toolbar={
        <ReportToolbar range={range} setRange={setRange} presets={presets}
          pdf={itemId ? { path: `/inventory/reports/stock-card/${itemId}/pdf`,
                          params: { from_date: range.from, to_date: range.to,
                                    store_id: store ? String(store) : undefined },
                          filename: `stock-card-${item?.code ?? itemId}.pdf` } : undefined}
          onExport={d ? () => downloadCsv(`stock-card-${d.item_code}-${range.from}-${range.to}`, [
            ['Date', 'Kind', 'Document', 'Particulars', 'Store', 'In', 'Out', 'Balance'],
            ['', '', '', 'Opening', '', '', '', d.opening],
            ...d.rows.map((r) => [r.date, r.kind, r.reference ?? '', r.particulars ?? '',
              r.store_name, r.qty_in, r.qty_out, r.balance]),
          ]) : undefined}>
          <Box sx={{ minWidth: 280 }}>
            <ItemPicker options={items.data?.items ?? []} value={item} storeId={store}
              label={t('inv.item')} onPick={(it) => set({ item: it ? String(it.id) : null })} />
          </Box>
          <StoreSelect label={t('inv.store')} value={store} stores={stores.data ?? []}
            onChange={(v) => set({ store: v ? String(v) : null })} allowAll sx={{ minWidth: 200 }} />
        </ReportToolbar>
      }>
      {!itemId ? <Empty text={t('stock.pickItemFirst')} />
        : q.isError ? <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>
          : q.isLoading || !d ? <Loading /> : (
            <>
              <Typography sx={{ px: 2.5, pb: 1, fontWeight: 600 }} dir="auto">
                <span className="doc-no">{d.item_code}</span> · {nameOf(d, ar)}
                <span style={{ color: 'var(--ink-muted)', fontWeight: 400 }}>
                  {' — '}{d.store_name ?? t('stock.allStores')}</span>
              </Typography>
              <Box sx={{ px: 2, pb: 1, overflowX: 'auto' }}>
                <table className="rpt" style={{ minWidth: 820 }}>
                  <thead><tr>
                    <th style={{ width: 104 }}>{t('entry.date')}</th>
                    <th style={{ width: 130 }}>{t('stock.movement')}</th>
                    <th style={{ width: 140 }}>{t('report.voucherNo')}</th>
                    <th>{t('stock.particulars')}</th>
                    {!d.store_id && <th style={{ width: 140 }}>{t('inv.store')}</th>}
                    <th className="num" style={{ width: 90 }}>{t('stock.in')}</th>
                    <th className="num" style={{ width: 90 }}>{t('stock.out')}</th>
                    <th className="num" style={{ width: 110 }}>{t('stock.balance')}</th>
                  </tr></thead>
                  <tbody>
                    <tr>
                      <td colSpan={d.store_id ? 5 : 6} style={{ fontWeight: 600 }}>{t('stock.opening')}</td>
                      <td />
                      <td className="num" style={{ fontWeight: 600 }}>{fmtQty(d.opening)}</td>
                    </tr>
                    {d.rows.map((r, i) => (
                      <tr key={i} className={r.doc_id ? 'clickable' : undefined} onClick={() => open(r)}>
                        <td style={{ whiteSpace: 'nowrap' }}>{gregorian(r.date, i18n.language)}</td>
                        <td>{t(`stock.kind${r.kind}`)}</td>
                        <td><span className="doc-no" style={{ color: r.doc_id ? 'var(--primary-ink)' : undefined }}>
                          {r.reference}</span></td>
                        <td dir="auto">{r.particulars}</td>
                        {!d.store_id && <td>{r.store_name}</td>}
                        <td className="num">{Number(r.qty_in) ? fmtQty(r.qty_in) : ''}</td>
                        <td className="num">{Number(r.qty_out) ? fmtQty(r.qty_out) : ''}</td>
                        <td className="num" style={{ color: Number(r.balance) < 0 ? c.dangerInk : undefined }}>
                          {fmtQty(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={d.store_id ? 4 : 5}>{t('stock.closing')}</td>
                    <td className="num">{fmtQty(d.total_in)}</td>
                    <td className="num">{fmtQty(d.total_out)}</td>
                    <td className="num">{fmtQty(d.closing)} {d.base_unit}</td>
                  </tr></tfoot>
                </table>
              </Box>
              {d.rows.length === 0 && <Empty text={t('stock.noMoves')} />}
            </>
          )}
      <ReportFooter />
    </ReportShell>
  )
}

// --- van stock ---------------------------------------------------------------

export function VanStockPage() {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const { range, setRange, presets } = useDateRange()
  const q = useQuery({ queryKey: ['inv-van-stock'], queryFn: fetchVanStock })
  const vans = q.data?.vans ?? []
  return (
    <ReportShell titleKey="nav.vanStock" period={`${t('report.asOf')} ${gregorian(range.to, i18n.language)}`}
      toolbar={
        <ReportToolbar hideDates range={range} setRange={setRange} presets={presets}
          pdf={{ path: '/inventory/reports/van-stock.pdf', filename: `van-stock-${range.to}.pdf` }}
          onExport={() => downloadCsv(`van-stock-${range.to}`, [
            ['Van', 'Salesman', 'Code', 'Item', 'Quantity', 'Value'],
            ...vans.flatMap((v) => v.rows.map((r) => [v.name, v.salesman ?? '', r.item_code,
              r.item_name, r.qty, r.value ?? ''])),
          ])} />
      }>
      {q.isError ? <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>
        : q.isLoading ? <Loading /> : vans.length === 0 ? <Empty text={t('stock.noVans')} /> : (
          <Box sx={{ display: 'grid', gap: 2, px: 2, pb: 2,
                     gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
            {vans.map((v) => (
              <Paper key={v.store_id} variant="outlined" sx={{ overflow: 'hidden' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline"
                  sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${c.border}` }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{(ar && v.name_ar) || v.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
                      <span className="doc-no">{v.code}</span>
                      {v.salesman ? ` · ${v.salesman}` : ` · ${t('inv.noSalesman')}`}</Typography>
                  </Box>
                  {v.total_value !== undefined && (
                    <Typography className="num" sx={{ fontWeight: 600 }}>{fmtMoney(v.total_value)}</Typography>
                  )}
                </Stack>
                {v.rows.length === 0 ? <Empty text={t('stock.vanEmpty')} /> : (
                  <table className="rpt">
                    <tbody>
                      {v.rows.map((r) => (
                        <tr key={r.item_id}>
                          <td dir="auto"><span className="doc-no">{r.item_code}</span> · {nameOf(r, ar)}</td>
                          <td className="num" style={{ width: 150 }}>{packsOf(r.qty, r)}</td>
                          {r.value !== undefined && <td className="num" style={{ width: 110 }}>{fmtMoney(r.value)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Paper>
            ))}
          </Box>
        )}
      <ReportFooter />
    </ReportShell>
  )
}

// --- sales by item -----------------------------------------------------------

export function SalesByItemPage() {
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const ar = i18n.language === 'ar'
  const { range, setRange, presets } = useDateRange()
  const [store, setStore] = useState<number | ''>('')
  const [group, setGroup] = useState<number | ''>('')
  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const params = { from_date: range.from, to_date: range.to, store_id: store || undefined,
                   group_id: group || undefined }
  const q = useQuery({ queryKey: ['inv-sales-by-item', params], queryFn: () => fetchSalesByItem(params) })
  const rows = q.data?.rows ?? []
  const seesCost = q.data?.totals.cost !== undefined
  return (
    <ReportShell titleKey="nav.salesByItem" period={rangeLabel(range.from, range.to, i18n.language, t)}
      toolbar={
        <ReportToolbar range={range} setRange={setRange} presets={presets}
          pdf={{ path: '/inventory/reports/sales-by-item.pdf',
                 params: { ...Object.fromEntries(Object.entries(params).map(([k, v]) => [k, v ? String(v) : undefined])) },
                 filename: `sales-by-item-${range.from}-${range.to}.pdf` }}
          onExport={() => downloadCsv(`sales-by-item-${range.from}-${range.to}`, [
            ['Code', 'Item', 'Group', 'Qty (base)', 'Sales', ...(seesCost ? ['Cost', 'Profit', 'Margin %'] : []),
             'Invoices'],
            ...rows.map((r) => [r.item_code, r.item_name, r.group_name ?? '', r.qty, r.sales,
              ...(seesCost ? [r.cost ?? '', r.profit ?? '', r.margin ?? ''] : []), String(r.invoices)]),
          ])}>
          <StoreSelect label={t('inv.store')} value={store} stores={stores.data ?? []}
            onChange={setStore} allowAll sx={{ minWidth: 200 }} />
          <GroupSelect value={group} onChange={setGroup} />
        </ReportToolbar>
      }>
      {q.isError ? <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>
        : q.isLoading ? <Loading /> : (
          <>
            <FigureGrid>
              <Figure label={bi('stock.items')} value={String(rows.length)} />
              <Figure label={bi('inv.subtotal')} value={fmtMoney(q.data!.totals.sales)} strong={!seesCost} />
              {seesCost && <Figure label={bi('stock.cost')} value={fmtMoney(q.data!.totals.cost!)} />}
              {seesCost && <Figure label={bi('inv.grossProfit')} value={fmtMoney(q.data!.totals.profit!)} strong />}
            </FigureGrid>
            {rows.length === 0 ? <Empty text={t('report.nothingInRange')} /> : (
              <Box sx={{ px: 2, pb: 1, overflowX: 'auto' }}>
                <table className="rpt" style={{ minWidth: seesCost ? 880 : 620 }}>
                  <thead><tr>
                    <th style={{ width: 110 }}>{t('inv.code')}</th>
                    <th>{t('inv.item')}</th>
                    <th className="num" style={{ width: 150 }}>{t('inv.sold')}</th>
                    <th className="num" style={{ width: 120 }}>{t('inv.subtotal')}</th>
                    {seesCost && <th className="num" style={{ width: 110 }}>{t('stock.cost')}</th>}
                    {seesCost && <th className="num" style={{ width: 120 }}>{t('inv.grossProfit')}</th>}
                    {seesCost && <th className="num" style={{ width: 80 }}>%</th>}
                    <th className="num" style={{ width: 80 }}>{t('stock.invoices')}</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.item_id}>
                        <td><span className="doc-no">{r.item_code}</span></td>
                        <td dir="auto">{nameOf(r, ar)}</td>
                        <td className="num">{packsOf(r.qty, r)}</td>
                        <td className="num">{fmtMoney(r.sales)}</td>
                        {seesCost && <td className="num">{fmtMoney(r.cost!)}</td>}
                        {seesCost && <td className="num" style={{ color: Number(r.profit) < 0 ? c.dangerInk : undefined }}>
                          {fmtMoney(r.profit!)}</td>}
                        {seesCost && <td className="num" style={{ color: 'var(--ink-muted)' }}>{r.margin ?? ''}</td>}
                        <td className="num" style={{ color: 'var(--ink-muted)' }}>{r.invoices}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={3}>{t('report.total')}</td>
                    <td className="num">{fmtMoney(q.data!.totals.sales)}</td>
                    {seesCost && <td className="num">{fmtMoney(q.data!.totals.cost!)}</td>}
                    {seesCost && <td className="num">{fmtMoney(q.data!.totals.profit!)}</td>}
                    {seesCost && <td className="num">{q.data!.totals.margin ?? ''}</td>}
                    <td />
                  </tr></tfoot>
                </table>
              </Box>
            )}
          </>
        )}
      <Typography variant="caption" sx={{ display: 'block', px: 2.5, pb: 2, color: c.inkMuted }}>
        {t('stock.sbiNote')}</Typography>
      <ReportFooter />
    </ReportShell>
  )
}

// --- reorder -----------------------------------------------------------------

export function LowStockPage() {
  const { t, i18n } = useTranslation()
  const nav = useNavigate()
  const ar = i18n.language === 'ar'
  const { range, setRange, presets } = useDateRange()
  const q = useQuery({ queryKey: ['inv-low-stock'], queryFn: fetchLowStock })
  const rows = q.data?.rows ?? []
  return (
    <ReportShell titleKey="nav.lowStock" period={`${t('report.asOf')} ${gregorian(range.to, i18n.language)}`}
      toolbar={
        <ReportToolbar hideDates range={range} setRange={setRange} presets={presets}
          pdf={{ path: '/inventory/reports/low-stock.pdf', filename: `reorder-${range.to}.pdf` }}
          onExport={() => downloadCsv(`reorder-${range.to}`, [
            ['Code', 'Item', 'On hand', 'Reorder level', 'Short by', 'Base unit'],
            ...rows.map((r) => [r.item_code, r.item_name, r.qty, r.reorder_level, r.short, r.base_unit]),
          ])} />
      }>
      {q.isError ? <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>
        : q.isLoading ? <Loading /> : rows.length === 0 ? <Empty text={t('stock.nothingToReorder')} /> : (
          <Box sx={{ px: 2, pb: 1, overflowX: 'auto' }}>
            <table className="rpt" style={{ minWidth: 640 }}>
              <thead><tr>
                <th style={{ width: 110 }}>{t('inv.code')}</th>
                <th>{t('inv.item')}</th>
                <th className="num" style={{ width: 150 }}>{t('inv.onHand')}</th>
                <th className="num" style={{ width: 150 }}>{t('stock.reorderLevel')}</th>
                <th className="num" style={{ width: 150 }}>{t('stock.shortBy')}</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.item_id} className="clickable"
                    onClick={() => nav(`/inventory/stock-card?item=${r.item_id}`)}>
                    <td><span className="doc-no">{r.item_code}</span></td>
                    <td dir="auto">{nameOf(r, ar)}</td>
                    <td className="num" style={{ color: Number(r.qty) <= 0 ? c.dangerInk : undefined }}>
                      {packsOf(r.qty, r)}</td>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>{packsOf(r.reorder_level, r)}</td>
                    <td className="num" style={{ fontWeight: 600, color: c.warningInk }}>{packsOf(r.short, r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      <Typography variant="caption" sx={{ display: 'block', px: 2.5, pb: 2, color: c.inkMuted }}>
        {t('stock.reorderNote')}</Typography>
      <ReportFooter />
    </ReportShell>
  )
}
